import { Form, Row, Col, Select, Button, Anchor } from 'antd';
import { useEffect, useState } from 'react';
import fetchApi from '../../../utils/api/fetchApi';
import systematic from '../../../config/systematic';
import AntDraggableModal from '../../../utils/extend/AntDraggableModal';
import getList from '../../universal/getList';
import antMessage from '../../../utils/extend/AntdNotification';
import stringHandle from '../../../utils/baseLib/stringHandle';

const defaultService = systematic.bmService;

const Configs = () => {
    const [form] = Form.useForm();
    const [modalVisible, setModalVisible] = useState(true);
    const [nodesData, setNodesData] = useState<any[]>([]);
    const [currentNode, setCurrentNode] = useState<string>('general');

    // 通用配置相关数据
    const [filesServerData, setFilesServerData] = useState<any[]>([]);
    const [filesServiceList, setFilesServiceList] = useState<any[]>([]);
    const [emailsServiceList, setEmailsServiceList] = useState<any[]>([]);
    const [emailsFilesGroupData, setEmailsFilesGroupData] = useState<any[]>([]);

    // 文件配置相关数据
    const [filesGroupData, setFilesGroupData] = useState<any[]>([]);

    // 保存从接口获取的原始配置值，供 files 节点使用 general 的字段
    const [generalConfig, setGeneralConfig] = useState<any>({});

    useEffect(() => {
        setNodesData([
            { key: 'general', href: '#general', title: '通用', id: 'general' },
            { key: 'files', href: '#files', title: '文件', id: 'files' },
        ]);
        loadGeneralConfig();
    }, []);

    /** 根据服务器 ID 获取其下的服务列表 */
    const fetchServiceList = async (serverId: string): Promise<any[]> => {
        try {
            const res = await fetchApi.GET(defaultService, {
                apiId: 'filesServer',
                apiExtend: 'getDefaultServer',
                apiVariable: serverId,
            });
            if (res.success) {
                const { manageServiceUrl } = res.data.api;
                const serviceRes = await fetchApi.GET(manageServiceUrl, {
                    apiId: 'service',
                    apiExtend: 'showList',
                });
                if (serviceRes.success) return serviceRes.data;
            }
        } catch (err: any) {
            antMessage('error', '无法获取服务列表', err.message);
        }
        return [];
    };

    /** 根据服务器ID 和 服务ID 获取其下的文件组列表 */
    const fetchFilesGroupList = async (filesServerId: string, filesServiceId: string): Promise<any[]> => {
        try {
            const res = await fetchApi.GET(defaultService, {
                apiId: 'filesServer',
                apiExtend: 'getDefaultServer',
                apiVariable: filesServerId,
            });
            if (res.success) {
                const { manageServiceUrl } = res.data.api;
                const groupRes = await fetchApi.GET(manageServiceUrl, {
                    apiId: 'filesGroup',
                    apiExtend: 'showList',
                }, { serviceId: filesServiceId });
                if (groupRes.success) return groupRes.data;
            }
        } catch (err: any) {
            antMessage('error', '无法获取文件组列表', err.message);
        }
        return [];
    };

    /** 加载通用配置 */
    const loadGeneralConfig = async () => {
        try {
            const configRes = await fetchApi.GET(defaultService, {
                apiId: 'configNodes',
                apiExtend: 'getConfig',
                apiData: { id: 'config', nodeId: 'general' },
            });
            if (configRes.success) {
                const general = configRes.data.general || {};
                setGeneralConfig(general); // 保存到状态，供 files 节点使用
                form.setFieldsValue({
                    filesServerId: general.filesServerId,
                    filesServiceId: general.filesServiceId,
                    emailsServerId: general.emailsServerId,
                    emailsServiceId: general.emailsServiceId,
                    emailsFilesGroupId: general.emailsFilesGroupId, // 补充邮件文件组回显
                });
            }

            // 获取文件服务器列表
            const serverRes = await fetchApi.GET(defaultService, {
                apiId: 'filesServer',
                apiExtend: 'showList',
            });
            if (serverRes.success) setFilesServerData(serverRes.data);

            // 根据已保存的服务器 ID 获取初始服务列表
            // 注意：这里使用 local 变量 general（从 response 解构）而非状态 generalConfig
            const generalData = configRes?.success ? configRes.data.general || {} : {};
            const { filesServerId, emailsServerId, emailsServiceId } = generalData;
            if (filesServerId) {
                const list = await fetchServiceList(filesServerId);
                setFilesServiceList(list);
            }
            if (emailsServerId) {
                const list = await fetchServiceList(emailsServerId);
                setEmailsServiceList(list);
                // 如果已保存邮件服务ID，则预加载对应文件组选项
                if (emailsServiceId) {
                    const groupRes = await fetchFilesGroupList(emailsServerId, emailsServiceId);
                    setEmailsFilesGroupData(groupRes);
                }
            }
        } catch (err: any) {
            antMessage('error', '无法获取通用配置', err.message);
        }
    };

    /** 加载文件配置 */
    const loadFilesConfig = async () => {
        try {
            const configRes = await fetchApi.GET(defaultService, {
                apiId: 'configNodes',
                apiExtend: 'getConfig',
                apiData: { id: 'config', nodeId: 'files' },
            });
            if (configRes.success) {
                const files = configRes.data.files || {};
                form.setFieldsValue({
                    webCarouselAdFilesGroupId: files.webCarouselAdFilesGroupId,
                    webProductFilesGroupId: files.webProductFilesGroupId,
                    webProductPriceFilesGroupId: files.webProductPriceFilesGroupId,
                });
            }

            // 获取文件组数据（依赖通用配置中的服务器和服务 ID）
            const { filesServerId, filesServiceId } = generalConfig;
            if (filesServerId && filesServiceId) {
                const groupRes = await fetchFilesGroupList(filesServerId, filesServiceId);
                setFilesGroupData(groupRes);
            }
        } catch (err: any) {
            antMessage('error', '无法获取文件配置', err.message);
        }
    };

    /** 锚点切换时触发 */
    const handleNodeChange = (nodeId: string) => {
        nodeId = stringHandle.removeExtension(nodeId, '#', 3);
        setCurrentNode(nodeId);
        if (nodeId === 'general') {
            loadGeneralConfig();
        } else if (nodeId === 'files') {
            loadFilesConfig();
        }
    };

    /** 保存通用配置 */
    const saveGeneral = () => {
        const values = form.getFieldsValue([
            'filesServerId',
            'filesServiceId',
            'emailsServerId',
            'emailsServiceId',
            'emailsFilesGroupId', // 补充保存邮件文件组
        ]);
        fetchApi.PUT(defaultService, {
            apiId: 'configNodes',
            apiExtend: 'setConfig',
            apiData: {
                id: 'config',
                nodeId: 'general',
                configName: '系统设置',
                content: values,
            },
        })
            .then(res => {
                if (res.success) antMessage('success', '保存成功');
            })
            .catch(err => antMessage('error', '保存失败', err.message));
    };

    /** 保存文件配置 */
    const saveFiles = () => {
        const values = form.getFieldsValue([
            'webCarouselAdFilesGroupId',
            'webProductFilesGroupId',
            'webProductPriceFilesGroupId',
        ]);
        fetchApi.PUT(defaultService, {
            apiId: 'configNodes',
            apiExtend: 'setConfig',
            apiData: {
                id: 'config',
                nodeId: 'files',
                configName: '系统设置',
                content: values,
            },
        })
            .then(res => {
                if (res.success) antMessage('success', '保存成功');
            })
            .catch(err => antMessage('error', '保存失败', err.message));
    };

    const handleOk = () => setModalVisible(false);

    return (
        <AntDraggableModal
            title="系统设置"
            open={modalVisible}
            okText="关闭"
            onOk={handleOk}
            onCancel={handleOk}
            cancelButtonProps={{ style: { display: 'none' } }}
            closable={false}
            maskClosable
            width={800}
        >
            <Anchor
                affix={false}
                direction="horizontal"
                items={nodesData}
                onChange={handleNodeChange}
            />
            <br />
            <Form form={form} name="Edit">
                <Row gutter={[8, 0]}>
                    {currentNode === 'general' && (
                        <>
                            <Col span={12}>
                                <Form.Item label="文件服务器" name="filesServerId" tooltip="储存图片及文件的服务器">
                                    <Select
                                        showSearch
                                        placeholder="文件服务器"
                                        optionFilterProp="children"
                                        onChange={async (value: string) => {
                                            const list = await fetchServiceList(value);
                                            setFilesServiceList(list);
                                            form.setFieldValue('filesServiceId', undefined);
                                        }}
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.general(filesServerData, 'serverName')}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="邮件服务器" name="emailsServerId" tooltip="储存邮件文件的服务器">
                                    <Select
                                        showSearch
                                        placeholder="邮件服务器"
                                        optionFilterProp="children"
                                        onChange={async (value: string) => {
                                            const list = await fetchServiceList(value);
                                            setEmailsServiceList(list);
                                            form.setFieldValue('emailsServiceId', undefined);
                                            // 服务器切换时，清空邮件文件组，因为服务变了
                                            form.setFieldValue('emailsFilesGroupId', undefined);
                                        }}
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.general(filesServerData, 'serverName')}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="文件服务id" name="filesServiceId">
                                    <Select
                                        showSearch
                                        placeholder="文件服务id"
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.generalEx(filesServiceList, { itemName: 'serviceName' })}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="邮件服务id" name="emailsServiceId">
                                    <Select
                                        showSearch
                                        placeholder="邮件服务id"
                                        optionFilterProp="children"
                                        onChange={async (value: string) => {
                                            const emailsServerId = form.getFieldValue('emailsServerId');
                                            if (emailsServerId) {
                                                const groupRes = await fetchFilesGroupList(emailsServerId, value);
                                                setEmailsFilesGroupData(groupRes);
                                            }
                                            // 切换服务时清空已选的文件组
                                            form.setFieldValue('emailsFilesGroupId', undefined);
                                        }}
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.generalEx(emailsServiceList, { itemName: 'serviceName' })}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                {/* 此处留空是为了对齐布局，可删除或放置其他内容 */}
                            </Col>
                            <Col span={12}>
                                <Form.Item label="邮件文件组" name="emailsFilesGroupId">
                                    <Select
                                        showSearch
                                        placeholder="邮件文件组"
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.generalEx(emailsFilesGroupData, { itemName: 'groupName' })}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Button onClick={saveGeneral}>保存数据</Button>
                            </Col>
                        </>
                    )}
                    {currentNode === 'files' && (
                        <>
                            <Col span={12}>
                                <Form.Item label="图片广告组" name="webCarouselAdFilesGroupId">
                                    <Select
                                        showSearch
                                        placeholder="文件组"
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.general(filesGroupData, 'groupName')}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="产品图片组" name="webProductFilesGroupId">
                                    <Select
                                        showSearch
                                        placeholder="文件组"
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.general(filesGroupData, 'groupName')}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item label="产品价格图片组" name="webProductPriceFilesGroupId">
                                    <Select
                                        showSearch
                                        placeholder="文件组"
                                        optionFilterProp="children"
                                        filterOption={(input, option: any) =>
                                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {getList.general(filesGroupData, 'groupName')}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Button onClick={saveFiles}>保存数据</Button>
                            </Col>
                        </>
                    )}
                </Row>
            </Form>
        </AntDraggableModal>
    );
};

/** 获取默认文件服务器 */
Configs.getDefaultFilesServer = async () => {
    let res: any = { success: false, data: undefined };
    try {
        const configNodesRes = await fetchApi.GET(defaultService, {
            apiId: 'configNodes',
            apiExtend: 'getConfig',
            apiData: { id: 'config', nodeId: 'general' },
        });
        if (configNodesRes.success) {
            const { filesServerId } = configNodesRes.data.general || {};
            if (filesServerId) {
                const filesServerRes = await fetchApi.GET(defaultService, {
                    apiId: 'filesServer',
                    apiVariable: filesServerId,
                });
                res = filesServerRes;
            }
        }
    } catch (e) { /* ignore */ }
    return res;
};

export default Configs;